import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { createClient } from '@/lib/supabase/client'

interface UserProfile {
  id: string
  name: string
  email: string
  avatar_url: string | null
}

interface TeamData {
  id: string
  name: string
}

interface MembershipWithTeam {
  team_id: string
  role: string
  teams: TeamData | null
}

interface AuthContextType {
  user: User | null
  profile: UserProfile | null
  membership: MembershipWithTeam | null
  session: Session | null
  loading: boolean
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Create singleton outside component to prevent re-renders from creating new clients
const supabase = createClient()

// Timeout for auth initialization (10 seconds)
const AUTH_TIMEOUT_MS = 10000

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [membership, setMembership] = useState<MembershipWithTeam | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  // Track initialization to handle auth events properly
  const isInitialized = useRef(false)
  // Track if component is mounted to prevent state updates after unmount
  const isMounted = useRef(true)
  // Track timeout for cleanup on unmount
  const fetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchUserData = useCallback(async (userId: string) => {
    // Fetch profile
    const { data: profileData } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single()

    // Don't check isMounted here - the check in the outer callback is sufficient
    // Checking here causes race conditions with React Strict Mode cleanup
    if (profileData) {
      setProfile(profileData as UserProfile)
    }

    // Fetch membership with team
    const { data: membershipData } = await supabase
      .from('memberships')
      .select('*, teams(*)')
      .eq('user_id', userId)
      .single()

    if (membershipData) {
      setMembership(membershipData as MembershipWithTeam)
    }
  }, [])

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchUserData(user.id)
    }
  }, [user, fetchUserData])

  // Helper to safely complete initialization
  const completeInitialization = useCallback(() => {
    if (!isInitialized.current && isMounted.current) {
      isInitialized.current = true
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    isMounted.current = true

    // Timeout fallback: if initialization takes too long, assume no session
    const timeoutId = setTimeout(() => {
      if (!isInitialized.current) {
        console.warn('Auth initialization timed out after', AUTH_TIMEOUT_MS, 'ms')
        completeInitialization()
      }
    }, AUTH_TIMEOUT_MS)

    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        // Clear timeout since we got a response
        clearTimeout(timeoutId)

        if (!isMounted.current) return

        // Update session and user state immediately
        setSession(session)
        setUser(session?.user ?? null)

        // For initial session (page load), we need to fetch user data and set loading=false
        // For subsequent events (sign in/out), also fetch user data
        if (session?.user) {
          // Use setTimeout to ensure we're not blocking the auth state update
          // This avoids potential race conditions with the Supabase client
          fetchTimeoutRef.current = setTimeout(async () => {
            try {
              await fetchUserData(session.user.id)
            } catch (error) {
              // Handle AbortError and other errors gracefully
              if (error instanceof Error && error.name === 'AbortError') {
                // Request was aborted (navigation/unmount) - ignore
                return
              }
              console.error('Failed to fetch user data during auth state change:', error)
              // Still complete initialization even if profile fetch fails
              // User is authenticated, they can use the app
            } finally {
              completeInitialization()
            }
          }, 0)
        } else {
          if (isMounted.current) {
            setProfile(null)
            setMembership(null)
          }
          completeInitialization()
        }
      }
    )

    // Also call getSession for immediate initialization when there's no stored session
    // This handles the case where onAuthStateChange might not fire immediately
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        // Clear timeout on successful getSession, regardless of session presence
        // If session exists, onAuthStateChange will handle initialization
        clearTimeout(timeoutId)
        if (!session && !isInitialized.current) {
          completeInitialization()
        }
      })
      .catch((error) => {
        // Handle network errors, AbortError, etc.
        if (error instanceof Error && error.name === 'AbortError') {
          return
        }
        console.error('Failed to get initial session:', error)
        // On error, assume no session and let user try to log in
        clearTimeout(timeoutId)
        completeInitialization()
      })

    return () => {
      isMounted.current = false
      clearTimeout(timeoutId)
      subscription.unsubscribe()
      // Clear pending timeout to prevent state updates after unmount
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current)
      }
    }
  }, [fetchUserData, completeInitialization])

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        membership,
        session,
        loading,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
